import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronDown, ChevronUp, Search, Check, X, RefreshCw, AlertCircle, Loader2 } from 'lucide-react';
import { vendorService } from '../services/api';
import { useToast } from '../hooks/useToast';
import DateRangePicker from '../components/DateRangePicker';
import Pagination from '../components/Pagination';

const getInquiryStatusBadgeClass = (status) => {
  const s = (status || 'PENDING').toUpperCase();
  switch(s) {
    case 'NEW':
    case 'PENDING':
      return 'bg-blue-50 text-blue-700 border-blue-100';
    case 'HOT':
      return 'bg-emerald-50 text-emerald-700 border-emerald-100';
    case 'APPROVED':
    case 'WON':
      return 'bg-green-50 text-green-800 border-green-200';
    case 'WARM':
      return 'bg-orange-50 text-orange-700 border-orange-100';
    case 'COLD':
      return 'bg-slate-50 text-slate-650 border-slate-200';
    case 'REJECTED':
    case 'LOST':
      return 'bg-rose-50 text-rose-700 border-rose-100';
    default:
      return 'bg-gray-50 text-gray-700 border-gray-150';
  }
};

const getInquirySelectColorClass = (status) => {
  const s = (status || 'PENDING').toUpperCase();
  switch(s) {
    case 'NEW':
    case 'PENDING':
      return 'bg-blue-50 text-blue-700 border-blue-200';
    case 'HOT':
      return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    case 'WARM':
      return 'bg-orange-50 text-orange-700 border-orange-200';
    case 'COLD':
      return 'bg-slate-50 text-slate-650 border-slate-200';
    case 'REJECTED':
    case 'LOST':
      return 'bg-rose-50 text-rose-700 border-rose-200';
    case 'APPROVED':
    case 'WON':
      return 'bg-green-50 text-green-800 border-green-200';
    default:
      return 'bg-white text-gray-700 border-gray-200';
  }
};

const formatInquiryStatusLabel = (status) => {
  const s = (status || 'NEW').toUpperCase();
  switch(s) {
    case 'PENDING':
    case 'NEW':
      return 'New';
    case 'HOT':
      return 'Hot';
    case 'WARM':
      return 'Warm';
    case 'COLD':
      return 'Cold';
    case 'REJECTED':
    case 'LOST':
      return 'Lost';
    case 'APPROVED':
    case 'WON':
      return 'Won';
    default:
      return s;
  }
};


const Inquiries = () => {
  const { toast } = useToast();
  const [searchParams, setSearchParams] = useSearchParams();
  const pageParam = parseInt(searchParams.get('page'), 10);
  const initialPage = !isNaN(pageParam) && pageParam > 0 ? pageParam - 1 : 0;
  const sizeParam = parseInt(searchParams.get('size'), 10);
  const initialSize = !isNaN(sizeParam) && sizeParam > 0 ? sizeParam : 10;

  const [inquiries, setInquiries] = useState([]);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(initialPage);
  const [pageSize, setPageSize] = useState(initialSize);
  const [searchText, setSearchText] = useState('');
  const [statusFilter, setStatusFilter] = useState(''); // '', 'PENDING', 'APPROVED', 'REJECTED'
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [expandedId, setExpandedId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isAccordionOpen, setIsAccordionOpen] = useState(true); // main wrapper collapse

  const [hasListing, setHasListing] = useState(true);

  useEffect(() => {
    const pageParam = parseInt(searchParams.get('page'), 10);
    const p = !isNaN(pageParam) && pageParam > 0 ? pageParam - 1 : 0;
    const sizeParam = parseInt(searchParams.get('size'), 10);
    const s = !isNaN(sizeParam) && sizeParam > 0 ? sizeParam : 10;
    setCurrentPage(p);
    setPageSize(s);
  }, [searchParams]);

  const handlePageChange = (newPage) => {
    const totalPages = Math.ceil(total / pageSize);
    if (newPage >= 0 && (totalPages === 0 || newPage < totalPages)) {
      setCurrentPage(newPage);
      const newParams = new URLSearchParams(searchParams);
      if (newPage > 0) newParams.set('page', String(newPage + 1));
      else newParams.delete('page');
      if (pageSize !== 10) newParams.set('size', String(pageSize));
      else newParams.delete('size');
      setSearchParams(newParams);
    }
  };

  const handlePageSizeChange = (newSize) => {
    setPageSize(newSize);
    setCurrentPage(0);
    const newParams = new URLSearchParams(searchParams);
    newParams.delete('page');
    if (newSize !== 10) newParams.set('size', String(newSize));
    else newParams.delete('size');
    setSearchParams(newParams);
  };

  useEffect(() => {
    vendorService.getMyListings().then(res => {
      if (res.success && res.data) {
        const listings = res.data.listings || (Array.isArray(res.data) ? res.data : []);
        setHasListing(listings.length > 0);
      }
    }).catch(() => {});
  }, []);

  const fetchInquiries = async (overrideFilters = null) => {
    const startVal = overrideFilters ? overrideFilters.startDate : startDate;
    const endVal = overrideFilters ? overrideFilters.endDate : endDate;
    if ((startVal && !endVal) || (!startVal && endVal)) {
      return;
    }

    setLoading(true);
    try {
      const filters = {};
      const statusVal = overrideFilters ? overrideFilters.status : statusFilter;
      const searchVal = overrideFilters ? overrideFilters.keyword : searchText;
      const pageVal = overrideFilters ? overrideFilters.page : currentPage;
      const sizeVal = overrideFilters ? overrideFilters.pageSize : pageSize;

      if (statusVal) filters.status = statusVal;
      if (searchVal) filters.keyword = searchVal;
      if (startVal) filters.startDate = startVal;
      if (endVal) filters.endDate = endVal;

      const res = await vendorService.getMyInquiries(filters, pageVal, sizeVal);
      if (res.success && res.data) {
        setInquiries(res.data.inquiries || []);
        setTotal(res.data.total || 0);
      }
    } catch (err) {
      console.error("Error loading inquiries:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchInquiries();
  }, [currentPage, pageSize, statusFilter, startDate, endDate]);

  // Handle search submit/trigger
  const handleSearchKeyPress = (e) => {
    if (e.key === 'Enter') {
      setCurrentPage(0);
      fetchInquiries();
    }
  };

  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
  };

  const handleSearchSubmit = () => {
    setCurrentPage(0);
    fetchInquiries();
  };

  const handleStatusChange = async (inquiryId, newStatus) => {
    try {
      const res = await vendorService.updateInquiryStatus(inquiryId, newStatus);
      if (res.success) {
        // Update local list
        setInquiries(prev =>
          prev.map(inq => inq.id === inquiryId ? { ...inq, status: newStatus } : inq)
        );
        toast.success(`Inquiry status updated to ${newStatus.toLowerCase()} successfully.`);
      } else {
        toast.error("Failed to update status: " + res.error);
      }
    } catch (err) {
      console.error("Error updating status:", err);
      toast.error("An error occurred while updating status.");
    }
  };


  const toggleAccordion = (id) => {
    setExpandedId(prev => (prev === id ? null : id));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const totalPages = Math.ceil(total / pageSize);

  return (
    <div className="w-full">
      {/* Header */}
      <div className="border-b border-gray-100 pb-4 mb-6 space-y-3">
        <h2 className="text-xl font-bold text-gray-800">Inquiries</h2>
        {!hasListing && (
          <div className="flex items-center gap-3 bg-sky-50/40 border border-sky-100 rounded-xl p-4 text-xs md:text-sm text-sky-900">
            <AlertCircle className="text-[#2ab391] flex-shrink-0" size={18} />
            <span className="font-semibold font-sans">
              To receive inquiries from architects and designers, list your business.
            </span>
          </div>
        )}
      </div>

      {/* Responsive Filter Section */}
      <div className="bg-white border border-gray-200/80 rounded-2xl p-4 sm:p-5 shadow-xs mb-6">
        <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center">
          
          {/* 1. Searchbar (First on desktop, Line 1 on mobile) */}
          <div className="relative w-full md:flex-1">
            <span className="absolute inset-y-0 left-0 pl-3.5 flex items-center pointer-events-none text-gray-400">
              <Search size={14} />
            </span>
            <input
              type="text"
              value={searchText}
              onChange={handleSearchChange}
              onKeyDown={handleSearchKeyPress}
              placeholder="Search inquiries..."
              className="w-full bg-white border border-gray-200 rounded-xl pl-9 pr-9 py-2 text-xs text-gray-700 placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#2ab391]/20 focus:border-[#2ab391] transition-all h-10"
            />
            {searchText && (
              <button
                onClick={() => {
                  setSearchText('');
                  setCurrentPage(0);
                  fetchInquiries({
                    status: statusFilter,
                    keyword: '',
                    startDate: startDate,
                    endDate: endDate,
                    page: 0,
                    pageSize: pageSize
                  });
                }}
                className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600"
              >
                <X size={14} />
              </button>
            )}
          </div>

          {/* Line 2 on mobile: All Statuses & Date Range side-by-side in single line! */}
          <div className="grid grid-cols-2 gap-2.5 sm:gap-3 w-full md:w-auto md:flex md:items-center">
            {/* 2. All Statuses (Second on desktop) */}
            <div className="relative w-full md:w-44">
              <select
                value={statusFilter}
                onChange={(e) => {
                  setStatusFilter(e.target.value);
                  setCurrentPage(0);
                }}
                className="w-full bg-white border border-gray-200 rounded-xl pl-3 pr-7 py-2 text-xs font-semibold text-gray-700 focus:outline-none focus:ring-2 focus:ring-[#2ab391]/20 focus:border-[#2ab391] appearance-none cursor-pointer h-10 truncate"
              >
                <option value="">All Statuses</option>
                <option value="NEW">New</option>
                <option value="HOT">Hot</option>
                <option value="WARM">Warm</option>
                <option value="COLD">Cold</option>
                <option value="LOST">Lost</option>
                <option value="WON">Won</option>
              </select>
              <span className="absolute inset-y-0 right-0 pr-2.5 flex items-center pointer-events-none text-gray-400">
                <ChevronDown size={14} />
              </span>
            </div>

            {/* 3. Date Range Picker (Third on desktop) */}
            <div className="w-full md:w-52">
              <DateRangePicker
                startDate={startDate}
                endDate={endDate}
                onChange={(start, end) => {
                  setStartDate(start);
                  setEndDate(end);
                  setCurrentPage(0);
                }}
                placeholder="Select Date Range"
                className="w-full h-10 text-xs"
              />
            </div>
          </div>

        </div>
      </div>

      {/* Main Accordion Panel (Inquiry Details) */}
      <div className="border border-gray-200 rounded-2xl overflow-hidden shadow-xs bg-white mb-6">
        {/* Accordion header */}
        <div
          onClick={() => setIsAccordionOpen(!isAccordionOpen)}
          className="bg-gray-50/30 px-5 py-4 flex justify-between items-center border-b border-gray-200 cursor-pointer hover:bg-gray-50/80 transition-colors select-none"
        >
          <span className="font-bold text-gray-700 text-sm tracking-wide">
            Inquiry Details
          </span>
          <span className="text-[#2ab391]">
            {isAccordionOpen ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
          </span>
        </div>

        {/* Accordion Body contents */}
        {isAccordionOpen && (
          <div className="divide-y divide-gray-100">
            {loading ? (
              <div className="flex items-center justify-center py-12 gap-2 text-gray-500">
                <Loader2 size={22} className="animate-spin text-[#2ab391]" />
                <span className="text-xs font-medium text-gray-600">Loading...</span>
              </div>
            ) : inquiries.length === 0 ? (
              <div className="py-16 text-center text-gray-400 font-semibold text-sm">
                No inquiries found matching filters.
              </div>
            ) : (
              inquiries.map(inq => {
                const isExpanded = expandedId === inq.id;

                return (
                  <div key={inq.id} className="w-full border-b border-gray-100 last:border-b-0">
                    {/* Collapsed Inquiry Summary Row */}
                    <div
                      onClick={() => toggleAccordion(inq.id)}
                      className="px-4 sm:px-5 py-3.5 sm:py-4 flex items-center justify-between gap-3 sm:gap-4 cursor-pointer hover:bg-gray-50/30 transition-colors select-none"
                    >
                      <div className="flex-1 min-w-0 grid grid-cols-1 sm:grid-cols-2 gap-1 sm:gap-2 items-center">
                        {/* Name and Phone */}
                        <div className="min-w-0">
                          <div className="font-bold text-gray-800 text-sm truncate">{inq.name}</div>
                          <div className="text-xs text-gray-500 font-medium mt-0.5">{inq.phone}</div>
                        </div>

                        {/* Date */}
                        <div className="text-xs text-gray-500 font-medium">
                          {formatDate(inq.created_at)}
                        </div>
                      </div>

                      {/* Interactive Status Badge Dropdown & Chevron Caret */}
                      <div className="flex items-center gap-2 sm:gap-3 flex-shrink-0" onClick={(e) => e.stopPropagation()}>
                        <div className="relative">
                          <select
                            value={inq.status === 'PENDING' ? 'NEW' : inq.status === 'APPROVED' ? 'WON' : inq.status === 'REJECTED' ? 'LOST' : inq.status}
                            onChange={(e) => handleStatusChange(inq.id, e.target.value)}
                            className={`text-[10px] sm:text-[11px] font-bold uppercase tracking-wider pl-2.5 pr-6 py-1 rounded-full border cursor-pointer appearance-none focus:outline-none focus:ring-1 focus:ring-[#2ab391]/30 transition-all ${getInquirySelectColorClass(inq.status)}`}
                          >
                            <option value="NEW" className="bg-white text-blue-600 font-semibold">New</option>
                            <option value="HOT" className="bg-white text-emerald-600 font-semibold">Hot</option>
                            <option value="WARM" className="bg-white text-amber-600 font-semibold">Warm</option>
                            <option value="COLD" className="bg-white text-slate-600 font-semibold">Cold</option>
                            <option value="LOST" className="bg-white text-rose-600 font-semibold">Lost</option>
                            <option value="WON" className="bg-white text-green-600 font-semibold">Won</option>
                          </select>
                          <ChevronDown size={12} className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-current opacity-70" />
                        </div>

                        <button
                          type="button"
                          onClick={() => toggleAccordion(inq.id)}
                          className="text-gray-400 hover:text-gray-600 p-1"
                        >
                          {isExpanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                        </button>
                      </div>
                    </div>

                    {/* Expanded Accordion details */}
                    {isExpanded && (
                      <div className="bg-gray-50/20 px-4 sm:px-5 pb-4 sm:pb-5 pt-3 border-t border-gray-100 space-y-3">
                        <div>
                          <span className="block text-xs font-semibold text-gray-400 uppercase tracking-wider mb-1.5">
                            Inquiry Message
                          </span>
                          <span className="text-gray-700 text-xs font-medium leading-relaxed bg-white border border-gray-200/80 p-3 sm:p-3.5 rounded-xl block shadow-xs whitespace-pre-line break-words">
                            {inq.message || 'No message provided.'}
                          </span>
                        </div>
                      </div>
                    )}
                  </div>
                );
              })
            )}
          </div>
        )}
      </div>

      {/* Pagination controls */}
      <Pagination
        currentPage={currentPage}
        totalPages={Math.ceil(total / pageSize)}
        pageSize={pageSize}
        totalItems={total}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        pageSizeOptions={[10, 20, 50, 100]}
        className="mt-6"
      />
    </div>
  );
};

export default Inquiries;
